import type { CheckoutCycle, CheckoutPlan } from './billing-selection.js';

export type BillingSubscriptionSnapshot = {
  status: string;
  productId: string | null;
  plan?: CheckoutPlan | null;
  billingCycle?: CheckoutCycle | null;
  trialStart: string | null;
  trialEnd: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  providerSubscriptionId: string;
  provider: string;
} | null;

export type BillingVerificationSnapshot = {
  state?: 'pending' | 'verified_paid' | 'verified_unpaid' | 'failed';
  plan?: CheckoutPlan | null;
} | null;

export const BILLING_FAQS = [
  {
    id: 'faq-test-before-paying',
    question: 'Can I test before paying?',
    answer:
      'Yes. Create an account and confirm FramerSync works with your endpoint before starting a paid membership or lifetime purchase.',
  },
  {
    id: 'faq-upgrade',
    question: 'How do upgrades work?',
    answer:
      'Upgrades apply immediately. We prorate the current billing period so you only pay the difference for the remaining time.',
  },
  {
    id: 'faq-downgrade',
    question: 'How do downgrades work?',
    answer:
      'Downgrades are deferred until the end of your current billing cycle. Your current plan access stays active until that date.',
  },
  {
    id: 'faq-access',
    question: 'What access do the plans include?',
    answer:
      'Basic includes 1 project and unlimited collections. Pro and Lifetime include unlimited projects and unlimited collections.',
  },
  {
    id: 'faq-refunds',
    question: 'How do refunds work?',
    answer:
      'Paid plans do not include a free trial. Purchases are based on you confirming the app works with your endpoint before paying, so refunds are not provided except where required by law or agreed in writing.',
  },
  {
    id: 'faq-support',
    question: 'Where can I get billing help?',
    answer:
      'Email help@framer.parts and include your account email plus a short description of your request.',
  },
  {
    id: 'faq-invoices',
    question: 'Where do I find invoices and receipts?',
    answer:
      'Open Manage subscription to enter the Polar billing portal. Invoices, receipts, payment method updates, and billing history are available there.',
  },
];

const PLAN_PRICES = {
  basic: {
    monthly: 6.99,
    annual: 70,
  },
  pro: {
    monthly: 15,
    annual: 150,
  },
  lifetime: {
    single: 349,
  },
} as const;

export function formatPlanName(plan: CheckoutPlan | null | undefined): string {
  if (plan === 'basic') return 'Basic';
  if (plan === 'pro') return 'Pro';
  if (plan === 'lifetime') return 'Lifetime';
  return 'Unknown';
}

export function formatCycleName(cycle: CheckoutCycle | null | undefined): string {
  if (cycle === 'monthly') return 'Monthly';
  if (cycle === 'annual') return 'Yearly';
  return 'N/A';
}

export function formatStatusName(status: string | null | undefined): string {
  if (!status) {
    return 'No plan';
  }

  if (status === 'trialing') {
    return 'Active';
  }

  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function hasActiveSubscriptionStatus(status: string | null | undefined): boolean {
  if (!status) {
    return false;
  }

  return (
    status === 'active' || status === 'trialing' || status === 'past_due' || status === 'incomplete'
  );
}

export function hasPaidBillingAccess(
  subscription: BillingSubscriptionSnapshot,
  verification: BillingVerificationSnapshot,
): boolean {
  return (
    hasActiveSubscriptionStatus(subscription?.status ?? null) ||
    verification?.state === 'verified_paid'
  );
}

export function resolveEffectiveBillingPlan(
  subscription: BillingSubscriptionSnapshot,
  verification: BillingVerificationSnapshot,
): CheckoutPlan | null {
  if (!hasPaidBillingAccess(subscription, verification)) {
    return null;
  }

  return subscription?.plan ?? verification?.plan ?? null;
}

export function formatPlanSelectionLabel(plan: CheckoutPlan, billingCycle: CheckoutCycle): string {
  if (plan === 'lifetime') {
    return 'Lifetime Unlimited Access';
  }

  return `${formatPlanName(plan)} ${formatCycleName(billingCycle)}`;
}

export function formatPlanChargeLabel(
  plan: CheckoutPlan,
  billingCycle: CheckoutCycle | null,
): string {
  const price = getPlanPriceValue(plan, billingCycle);
  if (price == null) {
    return 'Pricing available in checkout';
  }

  if (plan === 'lifetime') {
    return `${formatCurrency(price)} one-time`;
  }

  return `${formatCurrency(price)} / ${billingCycle === 'annual' ? 'year' : 'month'}`;
}

export function getPlanFeatureSummary(plan: CheckoutPlan | null | undefined): string {
  if (plan === 'basic') {
    return 'One project. Unlimited collections.';
  }

  if (plan === 'pro') {
    return 'Unlimited projects. Unlimited collections.';
  }

  if (plan === 'lifetime') {
    return 'Unlimited access to every feature currently available in FramerSync.';
  }

  return 'Choose a plan to unlock FramerSync.';
}

export function getPlanPriceValue(
  plan: CheckoutPlan,
  billingCycle: CheckoutCycle | null,
): number | null {
  if (plan === 'lifetime') {
    return PLAN_PRICES.lifetime.single;
  }

  if (!billingCycle) {
    return null;
  }

  return PLAN_PRICES[plan][billingCycle];
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

export function formatRenewalLabel(dateValue: string | null): string | null {
  if (!dateValue) {
    return null;
  }

  const end = new Date(dateValue).getTime();
  const now = Date.now();
  if (Number.isNaN(end)) {
    return null;
  }

  const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) {
    return 'Today';
  }

  if (diffDays === 1) {
    return 'In 1 day';
  }

  return `In ${diffDays} days`;
}

export function formatAbsoluteDate(dateValue: string | null): string | null {
  if (!dateValue) {
    return null;
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toLocaleDateString();
}

export type ProrationEstimate = {
  amount: number;
  remainingDays: number;
};

export function estimateProration(params: {
  currentPlan: CheckoutPlan | null;
  currentCycle: CheckoutCycle | null;
  targetPlan: CheckoutPlan;
  targetCycle: CheckoutCycle | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
}): ProrationEstimate | null {
  const {
    currentPlan,
    currentCycle,
    targetPlan,
    targetCycle,
    currentPeriodStart,
    currentPeriodEnd,
  } = params;

  if (!currentPlan || !currentCycle || !targetCycle) {
    return null;
  }

  if (currentPlan === 'lifetime' || targetPlan === 'lifetime') {
    return null;
  }

  const periodStart = currentPeriodStart ? new Date(currentPeriodStart).getTime() : NaN;
  const periodEnd = currentPeriodEnd ? new Date(currentPeriodEnd).getTime() : NaN;
  const now = Date.now();

  if (
    Number.isNaN(periodStart) ||
    Number.isNaN(periodEnd) ||
    periodEnd <= now ||
    periodEnd <= periodStart
  ) {
    return null;
  }

  const currentPrice = getPlanPriceValue(currentPlan, currentCycle);
  const targetPrice = getPlanPriceValue(targetPlan, targetCycle);
  if (currentPrice == null || targetPrice == null) {
    return null;
  }

  const remainingFraction = Math.max(0, Math.min(1, (periodEnd - now) / (periodEnd - periodStart)));
  const remainingDays = Math.max(1, Math.ceil((periodEnd - now) / (1000 * 60 * 60 * 24)));

  if (currentCycle !== targetCycle) {
    if (currentCycle !== 'monthly' || targetCycle !== 'annual') {
      return null;
    }

    const unusedCurrentCredit = currentPrice * remainingFraction;
    const amount = Math.round((targetPrice - unusedCurrentCredit) * 100) / 100;
    if (amount <= 0) {
      return null;
    }

    return {
      amount,
      remainingDays,
    };
  }

  if (targetPrice <= currentPrice) {
    return null;
  }
  const amount = Math.round((targetPrice - currentPrice) * remainingFraction * 100) / 100;
  if (amount <= 0) {
    return null;
  }

  return {
    amount,
    remainingDays,
  };
}
